import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Star, Download, Trash2, ArrowLeft, Search, Calendar } from 'lucide-react';
import { materialsAPI } from '../services/api';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function History() {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    filterMaterials();
  }, [searchQuery, filterType, materials]);

  const loadHistory = async () => {
    try {
      const response = await materialsAPI.getHistory();
      setMaterials(response.data.materials);
      setFilteredMaterials(response.data.materials);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMaterials = () => {
    let filtered = materials;

    if (searchQuery) {
      filtered = filtered.filter(m =>
        m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.grade_level.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      if (filterType === 'favorites') {
        filtered = filtered.filter(m => m.is_favorite);
      } else {
        filtered = filtered.filter(m => m.material_type === filterType);
      }
    }

    setFilteredMaterials(filtered);
  };

  const toggleFavorite = async (id) => {
    try {
      const response = await materialsAPI.toggleFavorite(id);
      setMaterials(materials.map(m =>
        m.id === id ? { ...m, is_favorite: response.data.is_favorite } : m
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const deleteMaterial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;

    try {
      await materialsAPI.deleteMaterial(id);
      setMaterials(materials.filter(m => m.id !== id));
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  };

  const downloadTXT = (material) => {
    const content = `${material.title}\nType: ${material.material_type}\nGrade: ${material.grade_level}\nSubject: ${material.subject}\n\n${material.content}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${material.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  
  const downloadDOCX = async (material) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://teacher-material-backend-production.up.railway.app/api/materials/${material.id}/export/docx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${material.title.replace(/[^a-z0-9]/gi, '_')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DOCX download error:', error);
      alert('Failed to download DOCX');
    }
  };

const downloadPPTX = async (material) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://teacher-material-backend-production.up.railway.app/api/materials/${material.id}/export/pptx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${material.title.replace(/[^a-z0-9]/gi, '_')}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PPTX download error:', error);
      alert('Failed to download PPTX');
    }
  };
  const materialTypes = [...new Set(materials.map(m => m.material_type))];

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: '#2e7d32' }}>Loading your materials...</div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)'
    }}>
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              color: '#424242',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <Logo size="medium" showTagline={false} />
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <Search size={20} color="#999" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search by title, subject, or grade..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 48px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#81c784'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '200px'
              }}
            >
              <option value="all">All Materials</option>
              <option value="favorites">⭐ Favorites</option>
              {materialTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 8px 0' }}>Total Materials</p>
            <p style={{ fontSize: '32px', fontWeight: '700', color: '#2e7d32', margin: 0 }}>{materials.length}</p>
          </div>
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 8px 0' }}>Favorites</p>
            <p style={{ fontSize: '32px', fontWeight: '700', color: '#fbc02d', margin: 0 }}>
              {materials.filter(m => m.is_favorite).length}
            </p>
          </div>
        </div>

        {filteredMaterials.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <FileText size={64} color="#e0e0e0" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '20px', color: '#666', marginBottom: '8px' }}>
              {searchQuery || filterType !== 'all' ? 'No materials found' : 'No materials yet'}
            </h3>
            <p style={{ color: '#999' }}>
              {searchQuery || filterType !== 'all' ? 'Try adjusting your filters' : 'Generate some materials to see them here!'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <button
                onClick={() => navigate('/generate')}
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Generate Materials
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredMaterials.map((material) => (
              <DownloadCard
                key={material.id}
                material={material}
                onToggleFavorite={toggleFavorite}
                onDelete={deleteMaterial}
                onDownloadTXT={downloadTXT}
                onDownloadDOCX={downloadDOCX}
onDownloadPPTX={downloadPPTX}
              />
            ))}
          </div>
        )}
      </div>
<Footer />
    </div>
  );
}

function DownloadCard({ material, onToggleFavorite, onDelete, onDownloadTXT, onDownloadDOCX, onDownloadPPTX }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s'
      }}
      onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2e7d32', marginBottom: '8px' }}>
            {material.title}
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{
              padding: '4px 12px',
              background: '#e8f5e9',
              color: '#2e7d32',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {material.material_type}
            </span>
            <span style={{
              padding: '4px 12px',
              background: '#e3f2fd',
              color: '#42a5f5',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {material.grade_level}
            </span>
            <span style={{
              padding: '4px 12px',
              background: '#fff9c4',
              color: '#f57c00',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {material.subject}
            </span>
            <span style={{
              padding: '4px 12px',
              background: '#f5f5f5',
              color: '#666',
              borderRadius: '12px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Calendar size={12} />
              {new Date(material.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => onToggleFavorite(material.id)}
            style={{
              padding: '8px',
              background: material.is_favorite ? '#fff9c4' : 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            title={material.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star size={18} color={material.is_favorite ? '#fbc02d' : '#999'} fill={material.is_favorite ? '#fbc02d' : 'none'} />
          </button>

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              style={{
                padding: '8px',
                background: 'white',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              title="Download"
            >
              <Download size={18} color="#66bb6a" />
            </button>

            {showMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 10,
                  minWidth: '120px'
                }}
              >
                <button
                  onClick={() => { onDownloadTXT(material); setShowMenu(false); }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'white',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#424242'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseOut={(e) => e.target.style.background = 'white'}
                >
                  TXT
                </button>
                                <button
                  onClick={() => { onDownloadDOCX(material); setShowMenu(false); }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'white',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#424242',
                  }}
                  onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseOut={(e) => e.target.style.background = 'white'}
                >
                  DOCX
                </button>

<button
                          onClick={() => { onDownloadPPTX(material); setShowMenu(false); }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            background: 'white',
                            border: 'none',
                            borderTop: '1px solid #e0e0e0',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#424242',
                            borderBottomLeftRadius: '6px',
                            borderBottomRightRadius: '6px'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                          onMouseOut={(e) => e.target.style.background = 'white'}
                       >
                      PPTX
                    </button>
              </div>
            )}
          </div>

          <button
            onClick={() => onDelete(material.id)}
            style={{
              padding: '8px',
              background: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            title="Delete"
          >
            <Trash2 size={18} color="#ef5350" />
          </button>
        </div>
      </div>
      <div style={{
        padding: '16px',
        background: '#f5f5f5',
        borderRadius: '8px',
        maxHeight: '200px',
        overflowY: 'auto',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#424242'
      }} className="markdown-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{material.content}</ReactMarkdown>
      </div>
    </div>
  );
}